import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(input: string): string {
  const words = input.split(/\s+/).slice(0, 2);
  const initials = words.map((word) => word.charAt(0).toUpperCase()).join("");
  return initials;
}

export const isEmpty = <T>(val: T[] | Record<any, any>) => {
  return val.length === 0;
};

export const first = <T>(val: T[]) => {
  return val.length > 1 ? val[0] : undefined;
};

export const without = <T>(arr: T[], ...args: T[]) =>
  arr.filter((v) => !args.includes(v));

export const union = <T>(a: T[], b: T[]) => Array.from(new Set([...a, ...b]));

export const maxBy = <T>(arr: T[], by: keyof T) =>
  arr.sort((a, b) => (a[by] > b[by] ? -1 : a[by] < b[by] ? 1 : 0))[0];

type Func<T> = (...args: T[]) => any;

export const countBy = <T = any>(arr: T[], fn: Func<T> | string) => {
  const mapper = typeof fn === "function" ? fn : (val: any) => val[fn];
  return arr.reduce((acc, val) => {
    const value = mapper(val);
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {} as any);
};

export const uniqBy = <T>(arr: T[], key: keyof T): T[] => {
  return Object.values(
    arr.reduce(
      (map, item) => ({
        ...map,
        [`${item[key]}`]: item,
      }),
      {},
    ),
  );
};

type MapValuesToKeysIfAllowed<T> = {
  [K in keyof T]: T[K] extends PropertyKey ? K : never;
};
type Filter<T> = MapValuesToKeysIfAllowed<T>[keyof T];

export function groupBy<
  T extends Record<PropertyKey, any>,
  Key extends Filter<T>,
>(arr: T[], key: Key): Record<T[Key], T[]> {
  return arr.reduce(
    (accumulator, val) => {
      const groupedKey = val[key];
      if (!accumulator[groupedKey]) {
        accumulator[groupedKey] = [];
      }
      accumulator[groupedKey].push(val);
      return accumulator;
    },
    {} as Record<T[Key], T[]>,
  );
}

export const orderBy = <T>(
  arr: T[],
  props: (keyof T)[],
  orders?: ("asc" | "desc")[],
) =>
  [...arr].sort((a, b) =>
    props.reduce((acc, prop, i) => {
      if (acc === 0) {
        const [p1, p2] =
          orders && orders[i] === "desc"
            ? [b[prop], a[prop]]
            : [a[prop], b[prop]];
        acc = p1 > p2 ? 1 : p1 < p2 ? -1 : 0;
      }
      return acc;
    }, 0),
  );

export function getAspectRatio(width: number, height: number) {
  return parseFloat((width / height).toFixed(2));
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

export function getAspectRatioString(width: number, height: number): string {
  const commonDivisor = gcd(width, height);
  return `${width / commonDivisor}:${height / commonDivisor}`;
}

/**
 * Compares two arrays for equality regardless of order
 * @param a First array to compare
 * @param b Second array to compare
 * @returns boolean indicating if arrays contain the same elements
 */
export const listEquals = <T>(a: T[] = [], b: T[] = [], sameOrder = true): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = !sameOrder ? a : [...a].sort();
  const sortedB = !sameOrder ? b : [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};
